import { getFullMachineName } from '../machine';

const OTHER_BRAND_KEY = 'other';

const compareByName = (left, right) => (left.name || '').localeCompare(right.name || '');

const getPickQuantity = item => (Number(item?.quantity) || 0) + (Number(item?.extraQuantity) || 0);

/**
 * Fresh food for each stop of a run, in run order, for the driver apps.
 *
 * Expects service dockets with `order`, `freshBoxManufacturers { id name }`, `machine { assetId place location }`
 * and `pickList.pickListItems { quantity extraQuantity product { id name isFreshFood manufacturer { id name } } }`.
 *
 * - `boxes`: the supplier boxes the office ticked for the stop. Their pick lines are often 0 until the driver
 *   counts the box at the stop, so they are listed by brand and never as a quantity.
 * - `brands`: fresh pick list lines with a quantity, grouped by brand and summed per product.
 *
 * Stops without boxes or fresh lines are left out, so tenants that exclude fresh food from pick lists get [].
 */
export const buildFreshFoodByStop = (serviceDockets) => [...(serviceDockets || [])]
	.filter(Boolean)
	.sort((left, right) => (left.order ?? 0) - (right.order ?? 0))
	.map(serviceDocket => {
		const brandsByKey = new Map();

		(serviceDocket.pickList?.pickListItems || []).forEach(item => {
			const quantity = getPickQuantity(item);
			if (!item?.product?.isFreshFood || quantity <= 0) {
				return;
			}

			const { manufacturer } = item.product;
			const brandKey = manufacturer?.id || OTHER_BRAND_KEY;
			if (!brandsByKey.has(brandKey)) {
				brandsByKey.set(brandKey, {
					id: manufacturer?.id || null,
					name: manufacturer?.name || 'Other fresh food',
					itemsByProductId: new Map(),
				});
			}

			const { itemsByProductId } = brandsByKey.get(brandKey);
			const productItem = itemsByProductId.get(item.product.id);
			itemsByProductId.set(item.product.id, {
				productId: item.product.id,
				name: item.product.name,
				quantity: (productItem?.quantity || 0) + quantity,
			});
		});

		const brands = [...brandsByKey.values()]
			.map(({ itemsByProductId, ...brand }) => {
				const items = [...itemsByProductId.values()].sort(compareByName);
				return {
					...brand,
					items,
					totalQuantity: items.reduce((total, item) => total + item.quantity, 0),
				};
			})
			// Products without a brand go last.
			.sort((left, right) => Number(!left.id) - Number(!right.id) || compareByName(left, right));

		const boxes = (serviceDocket.freshBoxManufacturers || [])
			.filter(Boolean)
			.map(({ id, name }) => ({ id, name }))
			.sort(compareByName);

		const { machine } = serviceDocket;

		return {
			serviceDocketId: serviceDocket.id,
			machineId: machine?.id || null,
			name: getFullMachineName(machine, machine?.place, machine?.location),
			boxes,
			brands,
		};
	})
	.filter(stop => stop.boxes.length || stop.brands.length);

// The line already starts with the brand, so "Efoodz Chicken Katsu" reads "Chicken Katsu".
// Names where the brand is followed by something other than a word ("Big Crunch 2pk ...") stay whole.
const getNameWithoutBrand = (name, brandName) => {
	if (!name || !brandName || name.slice(0, brandName.length).toLowerCase() !== brandName.toLowerCase()) {
		return name;
	}

	const rest = name.slice(brandName.length);
	return /^\s+[a-z]/i.test(rest) ? rest.trim() : name;
};

// "2 Basil Pesto, 2 Sweet & Sour"
export const formatFreshFoodItems = brand => brand.items.map(item => `${item.quantity} ${getNameWithoutBrand(item.name, brand.name)}`).join(', ');

// Box pick lines stay at 0 until the driver counts the box, so never show a number for a box.
export const formatFreshFoodBoxLine = box => `${box.name} box: count it at the stop`;
